import type { AudioNoteStorageMethod, TranscriptionMethod } from '@renderer/chat/hooks/useAudioNoteSettings'
import { GlobalStore, GlobalState } from '@highlight/ipc'
import { DEFAULT_MEETING_MODE, type MeetingMode } from '@shared/types/AudioNoteSettings'
import { AudioNoteState } from '@shared/types/AudioNoteState'
import { isIncognitoModeEnabled } from '@shared/utils/incognito'
import { getMeetingSettingsForMode, resolveMeetingMode } from '@shared/utils/meetings'
import { isMac } from '@shared/utils/platform'
import type { AudioCaptureNeeds } from '@shared/types/AudioCapturePermissions'

export type TranscriptionRuntime = 'recall' | 'electron' | 'native'

/**
 * What a capture is for. Dictation also runs on a runtime (electron or native),
 * but as a plan it only ever needs the microphone, so it is its own kind.
 */
export type CapturePlan = TranscriptionRuntime | 'dictation'

const MIC_ONLY: AudioCaptureNeeds = { microphone: true, systemAudio: false, screenRecording: false }

/**
 * The capture permissions a plan needs. Single source of truth shared by the
 * main-process start gates and the renderer permission surfaces so they cannot
 * disagree.
 *
 * - `electron` recording: microphone + CATAP system audio (Chromium loopback).
 * - `recall` recording: microphone + Screen Recording (SDK captures the meeting window).
 * - `native` recording: microphone only.
 * - `dictation`: microphone only, on whichever runtime captures it.
 */
export function captureNeedsForPlan(plan: CapturePlan): AudioCaptureNeeds {
  switch (plan) {
    case 'electron':
      return { microphone: true, systemAudio: true, screenRecording: false }
    case 'recall':
      return { microphone: true, systemAudio: false, screenRecording: true }
    case 'native':
    case 'dictation':
      return MIC_ONLY
  }
}

export type TranscriptionProvider = 'deepgram' | 'elevenlabs'
export type TranscriptionModel = 'nova-3' | 'nova-2-meeting' | 'nova-2' | 'scribe_v2_realtime'

export const DEFAULT_TRANSCRIPTION_PROVIDER: TranscriptionProvider = 'deepgram'
export const DEFAULT_TRANSCRIPTION_MODEL: TranscriptionModel = 'nova-3'

export const AUDIO_NOTES_TRANSCRIPTION_METHOD_KEY = 'audio-notes.transcription-method'
export const AUDIO_NOTES_STORAGE_METHOD_KEY = 'audio-notes.storage-method'
export const AUDIO_NOTES_TRANSCRIPTION_PROVIDER_KEY = 'audio-notes.transcription-provider'
export const AUDIO_NOTES_TRANSCRIPTION_MODEL_KEY = 'audio-notes.transcription-model'
export const AUDIO_NOTES_TRANSCRIPTION_RUNTIME_OVERRIDE_KEY = 'audio-notes.transcription-runtime-override'

/**
 * When true, the electron audio runtime will substitute a non-Bluetooth
 * microphone (preferring a built-in laptop mic) whenever the default
 * input is the same Bluetooth device as the default output — opening
 * the mic on those devices flips the headset from A2DP to HFP and
 * collapses the system-loopback audio Highlight captures from 48 kHz
 * stereo to 24 kHz mono telephone quality. Off by default because the
 * substitution is opinionated (it may pick a USB mic the user didn't
 * expect) and only a small subset of users hit the underlying HFP
 * degradation in practice. Power users with the issue can opt in.
 */
export const AUDIO_NOTES_AVOID_HFP_MICS_KEY = 'audio-notes.avoid-hfp-mics'

/** Synchronous read of the avoid-HFP-mics setting. Used by the audio
 *  runtime's mic acquire path; the renderer toggle writes the same
 *  key via `useGlobalStore`. */
export const avoidHfpMicsEnabled = (): boolean => {
  return GlobalStore.settings().get<boolean>(AUDIO_NOTES_AVOID_HFP_MICS_KEY, false) ?? false
}

/**
 * When true, Highlight keeps recording the mic while Google Meet shows the user
 * muted *and* is merging audio with a nearby room device (Companion mode) —
 * there the laptop mic is the only path to the user's voice, so respecting the
 * mute means capturing nothing at all. Off by default: mute means mute.
 */
export const AUDIO_NOTES_CAPTURE_WHILE_MERGED_MUTED_KEY = 'audio-notes.capture-while-merged-muted'

/** Synchronous read of the Companion-mode capture setting, for the mic gate. */
export const captureWhileMergedMutedEnabled = (): boolean => {
  return GlobalStore.settings().get<boolean>(AUDIO_NOTES_CAPTURE_WHILE_MERGED_MUTED_KEY, false) ?? false
}

export const audioEnabled = () => {
  return GlobalStore.settings().get<boolean>('audio-features-enabled', true) ?? true
}

export const pttEnabled = () => {
  return GlobalStore.settings().get<boolean>('push-to-talk-enabled', false) ?? false
}

export const isTakingNotes = () => {
  const state = GlobalState.get<AudioNoteState>('audio-note-state')?.status
  return state === 'recording' || state === 'starting' || state === 'paused' || state === 'reconnecting'
}

export const pttActive = () => {
  return GlobalState.get<boolean>('ptt.pressed', false) ?? false
}

export const getTranscriptionRuntimeOverride = (): TranscriptionRuntime | undefined => {
  return GlobalStore.settings().get<TranscriptionRuntime>(AUDIO_NOTES_TRANSCRIPTION_RUNTIME_OVERRIDE_KEY)
}

/**
 * True when `runtime` is a valid choice for the given transcription `method`.
 * - 'local' method only supports the 'native' runtime.
 * - 'cloud' method supports 'electron' or 'recall'.
 */
const isRuntimeCompatibleWithMethod = (runtime: TranscriptionRuntime, method: TranscriptionMethod): boolean => {
  if (method === 'local') return runtime === 'native'
  return runtime === 'electron' || runtime === 'recall'
}

/**
 * Persist a runtime override. Rejects (logs and no-ops) combinations that
 * contradict the current transcription method — e.g. method='local' with
 * runtime='recall', or method='cloud' with runtime='native'. Throwing would
 * crash the settings UI that calls this; silent rejection keeps the stored
 * override consistent with the resolution logic in `getAudioMode`.
 */
export const setTranscriptionRuntimeOverride = (runtime: TranscriptionRuntime) => {
  const method = getTranscriptionMethod()
  if (!isRuntimeCompatibleWithMethod(runtime, method)) {
    console.error(
      `setTranscriptionRuntimeOverride: runtime '${runtime}' is incompatible with transcription method '${method}'; ignoring`,
    )
    return
  }
  GlobalStore.settings().set(AUDIO_NOTES_TRANSCRIPTION_RUNTIME_OVERRIDE_KEY, runtime)
}

export const clearTranscriptionRuntimeOverride = () => {
  GlobalStore.settings().delete(AUDIO_NOTES_TRANSCRIPTION_RUNTIME_OVERRIDE_KEY)
}

export const asrShouldRun = () => {
  if (isElectronAudioRuntime()) {
    return pttActive() || (audioEnabled() && isTakingNotes())
  }
  return pttActive() || (audioEnabled() && !isCloudTranscriptionEnabled() && isTakingNotes())
}

export const asrShouldBeInitialized = () => {
  if (isElectronAudioRuntime()) {
    return audioEnabled() || pttEnabled()
  }
  return (audioEnabled() && !isCloudTranscriptionEnabled()) || pttEnabled()
}

export const asrShouldTranscribe = () => {
  if (isElectronAudioRuntime()) {
    return isTakingNotes() || pttActive()
  }
  return (isTakingNotes() && !isCloudTranscriptionEnabled()) || pttActive()
}

export const asrIsRunning = () => {
  return GlobalState.get('asr.isRunning') ?? false
}

export const setAsrIsRunning = (isRunning: boolean) => {
  GlobalState.set('asr.isRunning', isRunning)
}

export const setIsInstallingASR = (isInstalling: boolean) => {
  GlobalState.set('asr.isInstalling', isInstalling)
}

export const isInstallingASR = () => {
  return GlobalState.get('asr.isInstalling') ?? false
}

export const getDeviceSupportsAsr = () => {
  return GlobalStore.device().get<boolean>('supportsAsr', false) ?? false
}

export const setDeviceSupportsAsr = (supportsAsr: boolean) => {
  GlobalStore.device().set('supportsAsr', supportsAsr)
}

export const isCloudTranscriptionEnabled = () => {
  return getTranscriptionMethod() === 'cloud'
}

export const getTranscriptionMethod = () => {
  return GlobalStore.settings().get<TranscriptionMethod>(AUDIO_NOTES_TRANSCRIPTION_METHOD_KEY, 'cloud') ?? 'cloud'
}

export const setAudioNoteStorage = (storage: AudioNoteStorageMethod) => {
  GlobalStore.settings().set(AUDIO_NOTES_STORAGE_METHOD_KEY, storage)
}

export const getAudioNoteStorage = () => {
  return GlobalStore.settings().get<AudioNoteStorageMethod>(AUDIO_NOTES_STORAGE_METHOD_KEY, 'cloud') ?? 'cloud'
}

export const isUsingCloudAudioNoteStorage = () => {
  return getAudioNoteStorage() === 'cloud'
}

export const getMeetingDetectionMode = (): MeetingMode => {
  return resolveMeetingMode(GlobalStore.settings().get('meetingMode', DEFAULT_MEETING_MODE))
}

export const setMeetingDetectionMode = (mode: MeetingMode) => {
  GlobalStore.settings().set('meetingMode', mode)
}

export const shouldAutoTranscribeMeetings = (): boolean => {
  if (isIncognitoModeEnabled()) {
    return false
  }
  return getMeetingSettingsForMode(getMeetingDetectionMode()).autoTranscribeMeetings
}

export const getTranscriptionProvider = (): TranscriptionProvider => {
  return GlobalStore.settings().get<TranscriptionProvider>(
    AUDIO_NOTES_TRANSCRIPTION_PROVIDER_KEY,
    DEFAULT_TRANSCRIPTION_PROVIDER,
  )
}

export const setTranscriptionProvider = (provider: TranscriptionProvider) => {
  GlobalStore.settings().set(AUDIO_NOTES_TRANSCRIPTION_PROVIDER_KEY, provider)
}

export const getTranscriptionModel = (): string => {
  return GlobalStore.settings().get<string>(AUDIO_NOTES_TRANSCRIPTION_MODEL_KEY, DEFAULT_TRANSCRIPTION_MODEL)
}

export const setTranscriptionModel = (model: string) => {
  GlobalStore.settings().set(AUDIO_NOTES_TRANSCRIPTION_MODEL_KEY, model)
}

// Must stay in sync with MeetingAppRegistry entries where electronRuntimeSupported: true.
// These are matched against the meeting's `app.name` (e.g. the Slack registry
// entry's app.name is 'Huddle', not 'Slack').
// Cannot import from @main directly because this file is shared between main and renderer.
// See MeetingAppRegistry in @main/utils/meetings/MeetingAppConfig.ts
const ELECTRON_RUNTIME_SUPPORTED_APPS = new Set(['Google Meet', 'Zoom', 'Huddle'])

/**
 * Resolved audio configuration: a `(method, runtime)` pair where the runtime is
 * guaranteed to be valid for the chosen method. This is the source of truth —
 * `getTranscriptionRuntime` is a thin compatibility wrapper.
 *
 * The two settings (transcription method + runtime override) are conceptually
 * one choice. Modeling them as a discriminated union here prevents nonsense
 * combinations like `method='local'` with `runtime='recall'` from leaking out
 * of the resolution logic.
 */
export type AudioMode = { method: 'local'; runtime: 'native' } | { method: 'cloud'; runtime: 'electron' | 'recall' }

export interface ResolveAudioModeOptions {
  method: TranscriptionMethod
  runtimeOverride?: TranscriptionRuntime
  meetingAppName?: string
  mac?: boolean
}

/**
 * Derive the runtime for a given (method, isMac, meetingApp) tuple, ignoring
 * any override. Encodes the default-routing rules.
 */
const deriveRuntime = (
  method: TranscriptionMethod,
  mac: boolean,
  meetingAppName: string | undefined,
): AudioMode['runtime'] => {
  if (method === 'local') return 'native'
  // On macOS prefer the Electron runtime for manual recordings (no meetingAppName)
  // and for meetings in apps we explicitly support; everything else falls back to Recall.
  if (mac && (!meetingAppName || ELECTRON_RUNTIME_SUPPORTED_APPS.has(meetingAppName))) return 'electron'
  return 'recall'
}

/**
 * Resolve the audio mode for the next operation.
 *
 * Resolution order:
 * 1. If an override is set AND compatible with the current method, the
 *    override wins (e.g. method='cloud' + override='recall' forces Recall
 *    even on a supported Mac app).
 * 2. Otherwise the runtime is derived from `(method, isMac, meetingAppName)`:
 *    - method='local' → 'native' (local ASR handles everything).
 *    - method='cloud' on non-macOS → 'recall'.
 *    - method='cloud' on macOS:
 *        - Manual recording (no `meetingAppName`) → 'electron'.
 *        - Supported meeting app (Google Meet, Zoom, Slack huddles) → 'electron'.
 *        - Unsupported meeting app (Teams, …) → 'recall'.
 *
 * Manual recordings on macOS intentionally route through the Electron runtime
 * because Recall is meeting-oriented and heavier-weight; Electron's loopback
 * capture is the right primitive for ad-hoc audio notes. To opt out, set the
 * explicit override (e.g. via the developer Audio Runtime setting) to 'recall'.
 *
 * @param meetingAppName - Optional app name (e.g. 'Google Meet', 'Zoom') of the
 *   meeting being recorded. Omit for manual recordings.
 */
export const resolveAudioMode = ({
  method,
  runtimeOverride,
  meetingAppName,
  mac = isMac,
}: ResolveAudioModeOptions): AudioMode => {
  const runtime =
    runtimeOverride && isRuntimeCompatibleWithMethod(runtimeOverride, method)
      ? runtimeOverride
      : deriveRuntime(method, mac, meetingAppName)
  // The branches above already enforce method/runtime compatibility, so the
  // cast here is sound — TypeScript can't narrow across the union split.
  return { method, runtime } as AudioMode
}

export const getAudioMode = (meetingAppName?: string): AudioMode => {
  return resolveAudioMode({
    method: getTranscriptionMethod(),
    runtimeOverride: getTranscriptionRuntimeOverride(),
    meetingAppName,
  })
}

/**
 * Backward-compatible accessor. New callers should prefer `getAudioMode` so
 * they can branch on the method too.
 */
export const getTranscriptionRuntime = (meetingAppName?: string): TranscriptionRuntime => {
  return getAudioMode(meetingAppName).runtime
}

export const isNativeAudioRuntime = () => getTranscriptionRuntime() === 'native'
export const isElectronAudioRuntime = () => getTranscriptionRuntime() === 'electron'

/**
 * True when the electron audio runtime could plausibly be used for the next
 * recording — i.e. the user hasn't opted out via override or turned off cloud
 * transcription on an unsupported platform. Used by the electron audio
 * runtime to decide whether to keep its hidden window alive across recordings.
 *
 * This is intentionally meeting-app-agnostic: it answers "could electron be
 * used at all?", not "will electron be used for this specific recording?".
 */
export const mightUseElectronRuntime = (): boolean => {
  const override = getTranscriptionRuntimeOverride()
  if (override === 'electron') return true
  if (override) return false
  return isMac && isCloudTranscriptionEnabled()
}
